// Page Événements - Salons, formations, networking du secteur BTP

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, MapPin, Clock, Users, ArrowLeft, Plus, X, Search, Filter,
  Heart, ExternalLink, Building2, Video, Euro, CheckCircle2, Star,
  ChevronRight, Globe, Ticket
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Types d'événements
const EVENT_TYPES = [
  { id: 'salon', label: 'Salon', icon: Building2, color: 'bg-purple-100 text-purple-700' },
  { id: 'formation', label: 'Formation', icon: Star, color: 'bg-blue-100 text-blue-700' },
  { id: 'networking', label: 'Networking', icon: Users, color: 'bg-green-100 text-green-700' },
  { id: 'conference', label: 'Conférence', icon: Video, color: 'bg-orange-100 text-orange-700' },
  { id: 'portes_ouvertes', label: 'Portes ouvertes', icon: Building2, color: 'bg-pink-100 text-pink-700' },
  { id: 'autre', label: 'Autre', icon: Calendar, color: 'bg-gray-100 text-gray-700' },
];

// Métiers pour le filtrage
const METIERS = [
  'Électricien', 'Plombier', 'Maçon', 'Carreleur', 'Peintre', 'Menuisier',
  'Charpentier', 'Couvreur', 'Chauffagiste', 'Climaticien', 'Plaquiste',
  'Pisciniste', 'Terrassier', 'Paysagiste', 'Cuisiniste', 'Façadier'
];

// Carte d'événement
function EventCard({ event, onInterested, onRegister, isInterested, isRegistered }) {
  const eventType = EVENT_TYPES.find(t => t.id === event.event_type) || EVENT_TYPES[5];
  const EventIcon = eventType.icon;
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };
  
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const isUpcoming = new Date(event.start_date) > new Date();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-purple-200 transition-colors"
      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
    >
      {/* Image de couverture */}
      {event.cover_image && (
        <div className="h-40 bg-gradient-to-br from-purple-500 to-blue-500 relative">
          <img 
            src={event.cover_image} 
            alt={event.title}
            className="w-full h-full object-cover"
          />
          {/* Badge type */}
          <div className="absolute top-3 left-3">
            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${eventType.color}`}>
              <EventIcon size={12} />
              {eventType.label}
            </span>
          </div>
          
          {/* Badge en ligne */}
          {event.is_online && (
            <div className="absolute top-3 right-3">
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white">
                <Globe size={12} />
                En ligne
              </span>
            </div>
          )}
        </div>
      )}
      
      <div className="p-5">
        {/* Header sans image */}
        {!event.cover_image && (
          <div className="flex items-center justify-between mb-3">
            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${eventType.color}`}>
              <EventIcon size={12} />
              {eventType.label}
            </span>
            {event.is_online && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                <Globe size={12} />
                En ligne
              </span>
            )}
          </div>
        )}
        
        {/* Titre et organisateur */}
        <h3 className="font-bold text-lg text-gray-900 mb-2">{event.title}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
        
        {/* Infos clés */}
        <div className="space-y-2 mb-4">
          {/* Date */}
          <div className="flex items-center gap-3 text-sm">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Calendar size={18} className="text-purple-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">{formatDate(event.start_date)}</p>
              <p className="text-gray-500">{formatTime(event.start_date)}</p>
            </div>
          </div>
          
          {/* Lieu */}
          <div className="flex items-center gap-3 text-sm">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              {event.is_online ? (
                <Video size={18} className="text-blue-600" />
              ) : (
                <MapPin size={18} className="text-blue-600" />
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {event.is_online ? 'Événement en ligne' : event.location_name}
              </p>
              {!event.is_online && (
                <p className="text-gray-500">{event.city}</p>
              )}
            </div>
          </div>
          
          {/* Prix */}
          {event.price > 0 ? (
            <div className="flex items-center gap-3 text-sm">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Euro size={18} className="text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{event.price.toFixed(2)} €</p>
                <p className="text-gray-500">Inscription payante</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-sm">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Ticket size={18} className="text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-green-600">Gratuit</p>
                <p className="text-gray-500">Entrée libre</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Métiers concernés */}
        {event.metiers && event.metiers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {event.metiers.slice(0, 3).map((metier, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                {metier}
              </span>
            ))}
            {event.metiers.length > 3 && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                +{event.metiers.length - 3}
              </span>
            )}
          </div>
        )}
        
        {/* Stats et actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Heart size={14} className={isInterested ? 'fill-red-500 text-red-500' : ''} />
              {event.interested_count || 0} intéressés
            </span>
            <span className="flex items-center gap-1">
              <Users size={14} />
              {event.participants_count || 0} inscrits
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => onInterested(event.id)}
              className={`p-2 rounded-xl transition-colors ${
                isInterested 
                  ? 'bg-red-100 text-red-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Heart size={18} className={isInterested ? 'fill-current' : ''} />
            </button>
            
            {event.registration_required ? (
              event.registration_link ? (
                <a
                  href={event.registration_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 text-white text-sm font-medium hover:opacity-90"
                >
                  S'inscrire
                  <ExternalLink size={14} />
                </a>
              ) : (
                <Button
                  onClick={() => onRegister(event.id)}
                  size="sm"
                  className={`rounded-xl ${
                    isRegistered 
                      ? 'bg-green-600' 
                      : 'bg-gradient-to-r from-purple-600 to-purple-700'
                  }`}
                >
                  {isRegistered ? (
                    <>
                      <CheckCircle2 size={14} className="mr-1" />
                      Inscrit
                    </>
                  ) : (
                    "S'inscrire"
                  )}
                </Button>
              )
            ) : (
              <span className="text-sm text-gray-500 italic">Sans inscription</span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Modal de création d'événement
function CreateEventModal({ onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'autre',
    start_date: '',
    start_time: '09:00',
    end_date: '',
    end_time: '',
    location_name: '',
    address: '',
    city: '',
    postal_code: '',
    is_online: false,
    online_link: '',
    registration_required: false,
    registration_link: '',
    max_participants: '',
    price: '',
    metiers: []
  });
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.start_date || (!formData.is_online && (!formData.city || !formData.location_name))) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    setLoading(true);
    
    // Formater les dates
    const startDateTime = `${formData.start_date}T${formData.start_time}:00`;
    const endDateTime = formData.end_date ? `${formData.end_date}T${formData.end_time || '18:00'}:00` : null;
    
    await onSubmit({
      ...formData,
      start_date: startDateTime,
      end_date: endDateTime,
      price: formData.price ? parseFloat(formData.price) : 0,
      max_participants: formData.max_participants ? parseInt(formData.max_participants) : null
    });
    setLoading(false);
  };
  
  const toggleMetier = (metier) => {
    setFormData(prev => ({
      ...prev,
      metiers: prev.metiers.includes(metier)
        ? prev.metiers.filter(m => m !== metier)
        : [...prev.metiers, metier]
    }));
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Créer un événement</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titre de l'événement *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Salon du Bâtiment 2025"
                className="rounded-xl"
              />
            </div>
            
            {/* Type d'événement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type d'événement
              </label>
              <div className="flex flex-wrap gap-2">
                {EVENT_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, event_type: type.id })}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      formData.event_type === type.id
                        ? type.color
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <type.icon size={14} />
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                placeholder="Décris ton événement..."
              />
            </div>
            
            {/* Date et heure */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de début *
                </label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heure de début
                </label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            
            {/* Événement en ligne */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_online}
                onChange={(e) => setFormData({ ...formData, is_online: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-gray-700">Événement en ligne</span>
            </label>
            
            {formData.is_online ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lien de l'événement en ligne
                </label>
                <Input
                  type="url"
                  value={formData.online_link}
                  onChange={(e) => setFormData({ ...formData, online_link: e.target.value })}
                  placeholder="https://zoom.us/..."
                  className="rounded-xl"
                />
              </div>
            ) : (
              <>
                {/* Lieu */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du lieu *
                  </label>
                  <Input
                    value={formData.location_name}
                    onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                    placeholder="Ex: Parc des Expositions"
                    className="rounded-xl"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 rue..."
                    className="rounded-xl"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville *
                    </label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Paris"
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal
                    </label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="75001"
                      className="rounded-xl"
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Inscription */}
            <div className="pt-4 border-t">
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={formData.registration_required}
                  onChange={(e) => setFormData({ ...formData, registration_required: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-gray-700">Inscription requise</span>
              </label>
              
              {formData.registration_required && (
                <div className="space-y-3 pl-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lien d'inscription externe (optionnel)
                    </label>
                    <Input
                      type="url"
                      value={formData.registration_link}
                      onChange={(e) => setFormData({ ...formData, registration_link: e.target.value })}
                      placeholder="https://eventbrite.com/..."
                      className="rounded-xl"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Places max
                      </label>
                      <Input
                        type="number"
                        value={formData.max_participants}
                        onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                        placeholder="100"
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prix (€)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="0 = gratuit"
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Métiers concernés */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Métiers concernés (optionnel)
              </label>
              <div className="flex flex-wrap gap-2">
                {METIERS.map(metier => (
                  <button
                    key={metier}
                    type="button"
                    onClick={() => toggleMetier(metier)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      formData.metiers.includes(metier)
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {metier}
                  </button>
                ))}
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full rounded-xl bg-gradient-to-r from-purple-600 to-purple-700"
              disabled={loading}
            >
              {loading ? 'Création...' : 'Créer l\'événement'}
            </Button>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Page principale
export default function EventsPage() {
  const navigate = useNavigate();
  const { profile } = useOutletContext() || {};
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedMetier, setSelectedMetier] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userInterests, setUserInterests] = useState(new Set());
  const [userRegistrations, setUserRegistrations] = useState(new Set());
  
  const token = localStorage.getItem('ecosystem_token');
  
  const fetchEvents = useCallback(async () => {
    try {
      let url = `${API_URL}/api/ecosystem/events?limit=50`;
      if (searchQuery) url += `&q=${encodeURIComponent(searchQuery)}`;
      if (selectedType) url += `&event_type=${selectedType}`;
      if (selectedMetier) url += `&metier=${encodeURIComponent(selectedMetier)}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setEvents(data);
      }
    } catch (e) {
      console.error('Error fetching events:', e);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedType, selectedMetier]);
  
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  
  const handleCreateEvent = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        toast.success('Événement créé avec succès !');
        setShowCreateModal(false);
        fetchEvents();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la création');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
  };
  
  const handleInterested = async (eventId) => {
    if (!token) {
      toast.error('Connecte-toi pour marquer ton intérêt');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/events/${eventId}/interested`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUserInterests(prev => {
          const next = new Set(prev);
          if (data.is_interested) {
            next.add(eventId);
          } else {
            next.delete(eventId);
          }
          return next;
        });
        toast.success(data.message);
        fetchEvents(); // Refresh counts
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
  };
  
  const handleRegister = async (eventId) => {
    if (!token) {
      toast.error('Connecte-toi pour t\'inscrire');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/events/${eventId}/register`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setUserRegistrations(prev => new Set([...prev, eventId]));
        toast.success('Inscription confirmée !');
        fetchEvents();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de l\'inscription');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
  };
  
  return (
    <div className="min-h-screen bg-[#F5F7FB]" data-testid="events-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#7C3AED] to-[#9333EA] sticky top-0 z-40">
        <div className="px-4 py-3 flex items-center gap-3">
          <Link 
            to="/reseau"
            className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"
          >
            <ArrowLeft size={20} className="text-white" />
          </Link>
          
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg">Événements</h1>
            <p className="text-white/70 text-xs">{events.length} événements à venir</p>
          </div>
          
          <Button
            onClick={() => {
              if (!token) {
                toast.error('Connectez-vous pour créer un événement');
                navigate('/reseau/connexion');
                return;
              }
              setShowCreateModal(true);
            }}
            size="sm"
            className="bg-white text-purple-600 hover:bg-white/90 rounded-xl"
          >
            <Plus size={16} className="mr-1" />
            Créer
          </Button>
        </div>
        
        {/* Barre de recherche */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un événement..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/10 rounded-xl text-white placeholder-white/50 text-sm border border-white/20 focus:outline-none focus:bg-white/20"
            />
          </div>
        </div>
        
        {/* Filtres par type */}
        <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setSelectedType('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              !selectedType ? 'bg-white text-purple-600' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            Tous
          </button>
          {EVENT_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => setSelectedType(selectedType === type.id ? '' : type.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                selectedType === type.id
                  ? 'bg-white text-purple-600'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <type.icon size={12} />
              {type.label}
            </button>
          ))}
        </div>
      </header>
      
      {/* Liste des événements */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <motion.div 
              className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
          </div>
        ) : events.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-12 text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-purple-100 flex items-center justify-center">
              <Calendar size={40} className="text-purple-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Aucun événement trouvé</h3>
            <p className="text-gray-500 mb-6">
              {searchQuery || selectedType 
                ? 'Modifie tes critères de recherche'
                : 'Aucun événement à venir pour le moment'}
            </p>
            <Button
              onClick={() => {
                if (!token) {
                  toast.error('Connecte-toi pour créer un événement');
                  navigate('/reseau/connexion');
                  return;
                }
                setShowCreateModal(true);
              }}
              className="rounded-xl bg-gradient-to-r from-purple-600 to-purple-700"
            >
              <Plus size={18} className="mr-2" />
              Créer un événement
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onInterested={handleInterested}
                onRegister={handleRegister}
                isInterested={userInterests.has(event.id)}
                isRegistered={userRegistrations.has(event.id)}
              />
            ))}
          </div>
        )}
      </main>
      
      {/* Modal création */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateEventModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateEvent}
          />
        )}
      </AnimatePresence>
      
      {/* FAB Mobile */}
      <button
        onClick={() => {
          if (!token) {
            toast.error('Connectez-vous pour créer un événement');
            navigate('/reseau/connexion');
            return;
          }
          setShowCreateModal(true);
        }}
        className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-xl flex items-center justify-center lg:hidden"
      >
        <Plus size={26} />
      </button>
    </div>
  );
}
